'use client';

import type { Column, Table } from '@tanstack/react-table';
import * as React from 'react';

import { Icons } from '@shared/components/content/icons';
import { DataTableViewOptions } from '@shared/components/data-table/data-table-view-options';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { cn } from '@shared/lib/utils';
import { DataTableBooleanFilter } from './data-table-boolean-filter';
import { DataTableCascadingFilter } from './data-table-cascading-filter';
import { DataTableDateFilter } from './data-table-date-filter';
import { DataTableFacetedFilter } from './data-table-faceted-filter';
import { DataTableFiltersPanel } from './data-table-filters-panel';
import { DataTablePeriodFilter } from './data-table-period-filter';
import { DataTableSliderFilter } from './data-table-slider-filter';

interface DataTableToolbarProps<TData> extends React.ComponentProps<'div'> {
  table: Table<TData>;
  enableViewSelector?: boolean;
  filterOrder?: string[];
  /**
   * Column ids of the filters to show as quick-access pills, in this order.
   * When given, a Filters button at the end of the row opens a panel of every
   * filter, the pills included. Omit to show every filter inline.
   */
  primaryFilters?: string[];
}

export function DataTableToolbar<TData>({
  table,
  children,
  className,
  enableViewSelector = false,
  filterOrder,
  primaryFilters,
  ...props
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;

  const columns = React.useMemo(() => {
    const allColumns: any[] = [];

    for (const column of table.getAllColumns()) {
      if (column.getCanFilter()) {
        allColumns.push(column);
      }

      if ((column.columnDef as any).columns) {
        for (const nestedCol of (column.columnDef as any).columns) {
          if (
            nestedCol.enableColumnFilter !== false &&
            nestedCol.meta?.variant
          ) {
            const actualColumn = table.getColumn(nestedCol.id);
            if (actualColumn) {
              allColumns.push(actualColumn);
            }
          }
        }
      }
    }

    // Sort columns based on filterOrder if provided
    if (filterOrder && filterOrder.length > 0) {
      return allColumns.sort((a, b) => {
        const aIndex = filterOrder.indexOf(a.id);
        const bIndex = filterOrder.indexOf(b.id);

        // If both columns are in filterOrder, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }

        // If only 'a' is in filterOrder, it comes first
        if (aIndex !== -1 && bIndex === -1) {
          return -1;
        }

        // If only 'b' is in filterOrder, it comes first
        if (aIndex === -1 && bIndex !== -1) {
          return 1;
        }

        // If neither is in filterOrder, maintain original order
        return 0;
      });
    }

    return allColumns;
    // The column objects are rebuilt whenever the column definitions change,
    // which is how async filter options and trees reach the toolbar; keying on
    // the table alone would pin the first render's columns for good.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, table.options.columns, filterOrder]);

  // The quick-access pills when primaryFilters is set, in that order.
  const quickColumns = React.useMemo(() => {
    if (!primaryFilters) return columns;
    const byId = new Map(columns.map((column) => [column.id, column]));
    return primaryFilters.flatMap((id) => byId.get(id) ?? []);
  }, [columns, primaryFilters]);

  const onReset = React.useCallback(() => {
    table.resetColumnFilters();
  }, [table]);

  return (
    <div
      role="toolbar"
      aria-orientation="horizontal"
      className={cn(
        'flex w-full items-start justify-between gap-2 p-1',
        className
      )}
      {...props}
    >
      <div className="flex flex-1 flex-wrap items-center gap-2">
        {quickColumns.map((column) => (
          <DataTableToolbarFilter key={column.id} column={column} />
        ))}
        {primaryFilters && (
          <DataTableFiltersPanel
            // Quick pills first, in their order, then the rest.
            columns={[
              ...quickColumns,
              ...columns.filter((column) => !quickColumns.includes(column)),
            ]}
            onClearAll={onReset}
          />
        )}
        {isFiltered && (
          <Button
            aria-label="Reset filters"
            variant="outline"
            size="sm"
            className="border-dashed"
            onClick={onReset}
            icon={<Icons.x />}
          >
            Reset
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {children}
        {enableViewSelector && <DataTableViewOptions table={table} />}
      </div>
    </div>
  );
}
interface DataTableToolbarFilterProps<TData> {
  column: Column<TData>;
}

export function DataTableToolbarFilter<TData>({
  column,
}: DataTableToolbarFilterProps<TData>) {
  {
    const columnMeta = column.columnDef.meta;

    const onFilterRender = React.useCallback(() => {
      if (!columnMeta?.variant) return null;

      switch (columnMeta.variant) {
        case 'text':
          return (
            <Input
              placeholder={columnMeta.placeholder ?? columnMeta.label}
              value={(column.getFilterValue() as string) ?? ''}
              onChange={(event) => column.setFilterValue(event.target.value)}
              className="h-8 w-40 lg:w-56"
            />
          );

        case 'number':
          return (
            <div className="relative">
              <Input
                type="number"
                inputMode="numeric"
                placeholder={columnMeta.placeholder ?? columnMeta.label}
                value={(column.getFilterValue() as string) ?? ''}
                onChange={(event) => column.setFilterValue(event.target.value)}
                className={cn('h-8 w-[120px]', columnMeta.unit && 'pr-8')}
              />
              {columnMeta.unit && (
                <span className="absolute inset-y-0 right-0 flex items-center rounded-r-md bg-accent px-2 text-sm text-muted-foreground">
                  {columnMeta.unit}
                </span>
              )}
            </div>
          );

        case 'range':
          return (
            <DataTableSliderFilter
              column={column}
              title={columnMeta.label ?? column.id}
            />
          );

        case 'date':
        case 'dateRange':
          return (
            <DataTableDateFilter
              column={column}
              title={columnMeta.label ?? column.id}
              multiple={columnMeta.variant === 'dateRange'}
            />
          );

        case 'select':
        case 'multiSelect':
          return (
            <DataTableFacetedFilter
              column={column}
              title={columnMeta.label ?? column.id}
              options={columnMeta.options ?? []}
              multiple={columnMeta.variant === 'multiSelect'}
            />
          );

        case 'periodPicker':
          return (
            <DataTablePeriodFilter
              column={column}
              title={columnMeta.label ?? column.id}
              periods={columnMeta.periods ?? []}
            />
          );

        case 'boolean':
          return (
            <DataTableBooleanFilter
              column={column}
              title={columnMeta.label ?? column.id}
            />
          );

        case 'cascading':
          return (
            <DataTableCascadingFilter
              column={column}
              title={columnMeta.label ?? column.id}
              nodes={columnMeta.nodes ?? []}
              isLoading={columnMeta.nodesLoading}
            />
          );

        default:
          return null;
      }
    }, [column, columnMeta]);

    return onFilterRender();
  }
}
